/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import { useCallback, useRef, useState } from 'react';
import { ACCOUNT_RECOVERY_SETTINGS } from '../constants/accountRecovery';
import { parseScheduledDeletionError } from '../utils/accountRecovery';
import { useFlagEnabled } from './settings';

function logKeepAccount(event: string, payload?: Record<string, unknown>) {
  // `__DEV__` is undefined on Next.js web builds.
  if (typeof __DEV__ === 'undefined' || !__DEV__) return;
  console.log(`[KeepAccount] ${event}`, payload ?? {});
}

/** Status and error code only — the response body can carry user data. */
function summarizeLoginError(error: unknown) {
  const axiosLike = error as {
    response?: { status?: number; data?: { code?: string } };
  };
  return {
    status: axiosLike?.response?.status,
    code: axiosLike?.response?.data?.code,
  };
}

/**
 * Shared keep-account recovery UX for login failures.
 * Detects USER_SCHEDULED_FOR_DELETION, shows confirmation popup, retries with restore.
 * Yes closes the popup and runs the retry; the calling flow owns its loader and
 * any restore error snackbar.
 */
export type KeepAccountRecoveryHandlers = {
  tryHandleScheduledDeletion: (
    error: unknown,
    retryWithRestore: () => void,
  ) => boolean;
};

export function useKeepAccountRecovery(): KeepAccountRecoveryHandlers & {
  keepAccountVisible: boolean;
  deleteScheduledAt?: string;
  onKeepAccountNo: () => void;
  onKeepAccountYes: () => void;
} {
  const { enabled: isAccountRecoveryEnabled } = useFlagEnabled(
    ACCOUNT_RECOVERY_SETTINGS.enabled,
    true,
  );

  const [keepAccountVisible, setKeepAccountVisible] = useState(false);
  const [deleteScheduledAt, setDeleteScheduledAt] = useState<string>();
  const pendingRecoverLoginRef = useRef<(() => void) | null>(null);

  /** Returns true when the keep-account popup was shown (caller should skip snackbar). */
  const tryHandleScheduledDeletion = useCallback(
    (error: unknown, retryWithRestore: () => void) => {
      if (!isAccountRecoveryEnabled) {
        return false;
      }

      const scheduled = parseScheduledDeletionError(error);
      if (!scheduled) {
        return false;
      }

      logKeepAccount('showing keep-account popup', {
        error: summarizeLoginError(error),
        hasDeleteScheduledAt: Boolean(scheduled.deleteScheduledAt),
      });

      // Always re-open on a fresh login 403/409 (after No, Yes+error, or backdrop close).
      setDeleteScheduledAt(scheduled.deleteScheduledAt);
      pendingRecoverLoginRef.current = retryWithRestore;
      setKeepAccountVisible(true);
      return true;
    },
    [isAccountRecoveryEnabled],
  );

  const onKeepAccountNo = useCallback(() => {
    logKeepAccount('user chose No — stay on login');
    setKeepAccountVisible(false);
    pendingRecoverLoginRef.current = null;
  }, []);

  const onKeepAccountYes = useCallback(() => {
    const retry = pendingRecoverLoginRef.current;
    logKeepAccount('user chose Yes — retrying with restore');
    pendingRecoverLoginRef.current = null;
    setKeepAccountVisible(false);
    retry?.();
  }, []);

  return {
    keepAccountVisible,
    deleteScheduledAt,
    tryHandleScheduledDeletion,
    onKeepAccountNo,
    onKeepAccountYes,
  };
}
